// Dashboard Component
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  People,
  CalendarMonth,
  AttachMoney,
  Today,
  AccessTime,
  Phone,
  MoreVert,
  TrendingUp,
} from '@mui/icons-material';
import { StatCard } from '../common';
import { useCurrency } from '../../contexts/CurrencyContext';
import apiService from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    totalClients: 0,
    todayAppointments: 0,
    monthlyRevenue: 0,
    pendingAppointments: 0,
  });
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [recentClients, setRecentClients] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartLoading, setChartLoading] = useState(false);
  
  // Use currency context for proper currency formatting
  const { formatPrice: formatCurrency, currencySymbol } = useCurrency();
 // Custom Currency Icon Component
 const CurrencyIcon = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: 'inherit',
    }}
  >
    {currencySymbol}
  </Box>
);
  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchChartData(selectedYear);
  }, [selectedYear]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/dashboard');
      
      if (response.success) {
        // Map the API response to the expected format
        setStats({
          totalClients: response.data.total_clients || 0,
          todayAppointments: response.data.total_appointments || 0,
          monthlyRevenue: response.data.total_revenue || 0,
          pendingAppointments: response.data.pending_appointments || 0,
        });
        setTodayAppointments(response.data.recent_appointments || []);
        setRecentClients(response.data.recent_clients || []);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Dashboard API error:', err);
      // Set default values to prevent undefined errors
      setStats({
        totalClients: 0,
        todayAppointments: 0,
        monthlyRevenue: 0,
        pendingAppointments: 0,
      });
      setTodayAppointments([]);
      setRecentClients([]);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (year) => {
    try {
      setChartLoading(true);
      const [revenueResponse, expensesResponse] = await Promise.all([
        apiService.get(`/invoices/monthly-revenue?year=${year}`),
        apiService.get(`/expenses/monthly-expenses?year=${year}`)
      ]);

      // Create monthly data structure
      const monthlyData = [];
      for (let month = 1; month <= 12; month++) {
        const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'short' });
        
        // API returns month as string, so convert for comparison
        const revenue = revenueResponse.success && revenueResponse.data.monthly_revenue 
          ? revenueResponse.data.monthly_revenue.find(item => parseInt(item.month) === month)?.revenue || 0
          : 0;
        
        const expenses = expensesResponse.success && expensesResponse.data.monthly_expenses
          ? expensesResponse.data.monthly_expenses.find(item => parseInt(item.month) === month)?.expenses || 0
          : 0;

        monthlyData.push({
          month: monthName,
          revenue: parseFloat(revenue) || 0,
          expenses: parseFloat(expenses) || 0,
          profit: parseFloat(revenue) - parseFloat(expenses)
        });
      }

      setChartData(monthlyData);
    } catch (err) {
      console.error('Chart data fetch error:', err);
      // Set default empty data
      setChartData([]);
    } finally {
      setChartLoading(false);
    }
  };

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getAppointmentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: stats.totalClients,
      icon: <People />,
      color: '#dc2cb0',
      change: '+12%',
      changeType: 'positive',
    },
    {
      title: "Today's Appointments",
      value: stats.todayAppointments,
      icon: <CalendarMonth />,
      color: '#E91E63',
      change: '+5%',
      changeType: 'positive',
    },
    {
      title: 'Monthly Revenue (Collected)',
      value: formatCurrency(stats.monthlyRevenue),
      icon: <CurrencyIcon />,
      color: '#8E24AA',
      change: '+18%',
      changeType: 'positive',
    },
    {
      title: 'Pending Appointments',
      value: stats.pendingAppointments,
      icon: <AccessTime />,
      color: '#FF9800',
      change: '-3%',
      changeType: 'negative',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
   
      {/* Quick Stats */}
      <Box sx={{ width: '100%', mb: 3 }}>
        <Paper sx={{ p: 3 }}> 
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: 'repeat(2, 1fr)', 
              sm: 'repeat(4, 1fr)' 
            }, 
            gap: 3 
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {stats.totalClients}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Clients
              </Typography>
              <LinearProgress
                variant="determinate"
                value={75}
                sx={{ mt: 1, height: 4, borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="secondary.main">
                {stats.todayAppointments}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Today's Bookings
              </Typography>
              <LinearProgress
                variant="determinate"
                value={60}
                color="secondary"
                sx={{ mt: 1, height: 4, borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {formatCurrency(stats.monthlyRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monthly Revenue (Collected)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={85}
                color="success"
                sx={{ mt: 1, height: 4, borderRadius: 2 }}
              />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {stats.pendingAppointments}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Bookings
              </Typography>
              <LinearProgress
                variant="determinate"
                value={30}
                color="warning"
                sx={{ mt: 1, height: 4, borderRadius: 2 }}
              />
            </Box>
          </Box>
        </Paper>
      </Box>
   

      {/* Revenue vs Expenses Chart */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={2}>
              <TrendingUp sx={{ fontSize: 28, color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Monthly Collected Revenue vs Expenses
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Year"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {chartLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress size={40} />
            </Box>
          ) : chartData.length === 0 ? (
            <Box textAlign="center" py={4}>
              <TrendingUp sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No data available for the selected year
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value)}
                  domain={[0, 'dataMax + 1000']}
                />
                <Tooltip 
                  formatter={(value, name, props) => [
                    formatCurrency(value), 
                    props.dataKey === 'revenue' ? 'Revenue' : props.dataKey === 'expenses' ? 'Expenses' : 'Profit'
                  ]}
                  labelStyle={{ fontWeight: 'bold' }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#4CAF50"
                  strokeWidth={3}
                  dot={{ fill: '#4CAF50', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8 }}
                  name="Revenue"
                />
                <Line
                  type="monotone"
                  dataKey="expenses"
                  stroke="#F44336"
                  strokeWidth={3}
                  dot={{ fill: '#F44336', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 8 }}
                  name="Expenses"
                />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#2196F3"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ fill: '#2196F3', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Profit"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: '1fr', 
          md: '2fr 1fr' 
        }, 
        gap: 3, 
        mb: 3 
      }}>
        {/* Today's Appointments */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold">
                  Appointments
                </Typography>
                <Chip
                  icon={<Today />}
                  label={`${todayAppointments.length} appointments`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {todayAppointments.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <CalendarMonth sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No upcoming appointments
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {todayAppointments.map((appointment, index) => (
                    <ListItem
                      key={appointment.id || index}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2,
                        '&:last-child': { mb: 0 },
                      }}
                      secondaryAction={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={appointment.status || 'Pending'}
                            color={getAppointmentStatusColor(appointment.status)}
                            size="small"
                          />
                          <IconButton size="small">
                            <MoreVert />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {appointment.client_name?.charAt(0) || 'C'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {appointment.client_name || 'Unknown Client'}
                            </Typography>
                            <Chip
                              icon={<AccessTime />}
                              label={formatDate(appointment.appointment_date)}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Service: {appointment.service_name || 'Not specified'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Duration: {appointment.duration || 60} minutes
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Recent Clients */}
        <Box>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="bold">
                  Recent Clients
                </Typography>
                <Chip
                  icon={<People />}
                  label={`${recentClients.length} clients`}
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              {recentClients.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <People sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No recent clients
                  </Typography>
                </Box>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {recentClients.map((client, index) => (
                    <ListItem
                      key={client.id || index}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        mb: 2,
                        '&:last-child': { mb: 0 },
                      }}
                      secondaryAction={
                        <IconButton size="small" href={`tel:${client.mobile}`}>
                          <Phone />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          {client.first_name?.charAt(0) || 'C'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight="medium">
                            {`${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unknown Client'}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {client.mobile || 'No phone'}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              Added: {client.created_at ? new Date(client.created_at).toLocaleDateString() : 'Unknown'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

    </Box>
  );
};

export default Dashboard;
